import { useNavigate } from "react-router-dom";
import {
  ArrowRight, AlertTriangle, Clock, CheckCircle,
  CircleDot, ChevronRight, Compass
} from "lucide-react";

const STATUS_CONFIG = {
  blocked: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/20" },
  waiting: { icon: Clock, color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20" },
  in_progress: { icon: CircleDot, color: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20" },
  optional: { icon: Compass, color: "text-[#8B95A5]", bg: "bg-[rgba(255,255,255,0.03)]", border: "border-[rgba(255,255,255,0.06)]" },
  complete: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20" },
};

const PRIORITY_DOTS = {
  critical: "bg-red-400",
  high: "bg-orange-400",
  medium: "bg-amber-400",
  low: "bg-gray-400",
};

/**
 * GuidancePanel — Role-specific next-steps panel.
 * Shows what's waiting, what's blocked, and what's optional.
 */
const GuidancePanel = ({ steps = [], title = "Next Steps", compact = false }) => {
  const navigate = useNavigate();

  if (!steps || steps.length === 0) return null;

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] mb-4" data-testid="guidance-panel">
      <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.06)] flex items-center gap-2">
        <Compass size={14} className="text-[#C9A84C]" />
        <h3 className="text-white text-sm font-semibold">{title}</h3>
        <span className="text-[#8B95A5] text-[10px] ml-auto">{steps.length} item{steps.length !== 1 ? "s" : ""}</span>
      </div>
      <div className={compact ? "p-2" : "p-3"}>
        <div className="space-y-1.5">
          {steps.map(step => {
            const cfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.optional;
            const StatusIcon = cfg.icon;
            const dot = PRIORITY_DOTS[step.priority] || PRIORITY_DOTS.medium;

            return (
              <button
                key={step.id}
                onClick={() => step.action_path && navigate(step.action_path)}
                className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border ${cfg.border} ${cfg.bg} hover:opacity-90 transition-opacity group`}
                data-testid={`guidance-step-${step.id}`}
              >
                <StatusIcon size={14} className={cfg.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                    <span className="text-white text-xs font-medium truncate">{step.title}</span>
                  </div>
                  {!compact && (
                    <p className="text-[#8B95A5] text-[10px] mt-0.5 line-clamp-1">{step.description}</p>
                  )}
                </div>
                {step.action_label && (
                  <span className="text-[#36A9E1] text-[10px] font-medium shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {step.action_label} <ChevronRight size={10} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GuidancePanel;
